// Conta: dados da empresa (o titular edita), situação do teste ou da
// assinatura, quem usa a conta, os dados da empresa (cópia, fotos, exclusão;
// LGPD) e sair.
import { MESES_ATE_APAGAR, RESPONSAVEL } from '../config.js';
import { anexos as anexosDados, atualizarEmpresa as salvarEmpresa, dadosDaCopia } from '../dados.js';
import { FUSOS } from '../lib/datas.js';
import { h, icone, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { anosDasFotos, arquivosDaCopia, fotosDoAno, nomeDaCopia } from '../lib/exportar.js';
import { celular as formatarCelular, data, documento as formatarDocumento, plural } from '../lib/formato.js';
import { montarZip } from '../lib/pacote.js';
import { rotuloPapel } from '../lib/papeis.js';
import { entregarArquivo } from '../lib/recibo.js';
import { emailValido, normalizarCelular, UFS } from '../lib/validacao.js';
import { ir, nestaTela } from '../rotas.js';
import { atualizarEmpresa, contaGravavel, diasDeTeste, ehTitular, hojeDaEmpresa, prazoDaFatura, sair, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { confirmarSaida } from '../ui/sinal.js';
import { avisar, campo, cartaoLink, faixa, formulario, secao } from '../ui/componentes.js';

const SITUACAO = {
  teste: 'Teste grátis',
  ativa: 'Assinatura ativa',
  somente_leitura: 'Só para consulta',
  cancelada: 'Cancelada',
};

export function telaConta() {
  const tela = nestaTela();
  const e = sessao.empresa;
  const dias = diasDeTeste();
  const prazo = prazoDaFatura();
  const situacao = (e.situacao === 'teste'
    ? (dias < 0 ? 'Teste grátis encerrado' : `Teste grátis até ${data(e.teste_ate)} (${dias === 0 ? 'último dia' : `faltam ${plural(dias, 'dia')}`})`)
    : SITUACAO[e.situacao] ?? e.situacao)
    + (prazo ? ` · fatura vencida: pague até ${data(prazo)}` : '')
    + (!prazo && e.situacao === 'ativa' && !contaGravavel() ? ' · fatura atrasada, só para consulta' : '');

  const dadosFixos = h('dl', { class: 'linha-dados' },
    h('dt', null, e.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'), h('dd', null, formatarDocumento(e.documento)),
    h('dt', null, 'Situação'), h('dd', null, situacao));

  let empresa;
  if (ehTitular()) {
    empresa = formulario({
      filhos: [
        campo({ rotulo: e.tipo_pessoa === 'PF' ? 'Nome completo' : 'Razão social', nome: 'nome', obrigatorio: true, valor: e.nome, formato: e.tipo_pessoa === 'PF' ? 'nome' : 'texto' }),
        campo({ rotulo: 'Nome fantasia', nome: 'nome_fantasia', valor: e.nome_fantasia ?? '', dica: 'É o nome que aparece no topo do app.' }),
        campo({ rotulo: 'Celular com DDD', nome: 'celular', tipo: 'tel', obrigatorio: true, valor: formatarCelular(e.celular), atributos: { inputmode: 'tel' } }),
        campo({ rotulo: 'E-mail', nome: 'email', tipo: 'email', valor: e.email ?? '', atributos: { inputmode: 'email', autocapitalize: 'none' } }),
        campo({ rotulo: 'Endereço', nome: 'endereco', valor: e.endereco ?? '' }),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Cidade', nome: 'cidade', valor: e.cidade ?? '', formato: 'nome' }),
          campo({ rotulo: 'UF', nome: 'uf', tipo: 'select', valor: e.uf ?? '', opcoes: [['', '—'], ...UFS.map((u) => [u, u])] })),
        campo({ rotulo: 'RNTRC', nome: 'rntrc', valor: e.rntrc ?? '', formato: 'rntrc', atributos: { inputmode: 'numeric' } }),
        campo({
          rotulo: 'Relatório para o contador',
          nome: 'relatorio_contabil',
          tipo: 'segmentos',
          valor: e.relatorio_contabil,
          opcoes: [['competencia', 'Competência', 'data da carga e da despesa'], ['caixa', 'Caixa', 'data em que o dinheiro entrou ou saiu']],
          dica: 'Pergunte ao seu contador qual ele prefere. Dá para mudar depois.',
        }),
        campo({ rotulo: 'Fuso horário', nome: 'fuso_horario', tipo: 'select', valor: e.fuso_horario, opcoes: FUSOS }),
        campo({
          rotulo: 'Resumo da semana por e-mail',
          nome: 'resumo_por_email',
          tipo: 'segmentos',
          valor: e.resumo_por_email === false ? 'nao' : 'sim',
          opcoes: [['sim', 'Receber', 'toda segunda de manhã'], ['nao', 'Não receber']],
          dica: sessao.usuario?.email ? `Vai para ${sessao.usuario.email}, o e-mail com que você entra no app.` : 'Vai para o e-mail com que você entra no app.',
        }),
      ],
      textoBotao: 'Salvar dados da empresa',
      validar: (d) => {
        const erros = {};
        if (d.nome.length < 2) erros.nome = 'Informe o nome.';
        if (!normalizarCelular(d.celular)) erros.celular = 'Informe DDD + número.';
        if (d.email && !emailValido(d.email)) erros.email = 'E-mail inválido.';
        return erros;
      },
      aoEnviar: async (d) => {
        const atualizada = await salvarEmpresa({
          nome: d.nome,
          nome_fantasia: d.nome_fantasia || null,
          celular: normalizarCelular(d.celular),
          email: d.email || null,
          endereco: d.endereco || null,
          cidade: d.cidade || null,
          uf: d.uf || null,
          rntrc: d.rntrc || null,
          relatorio_contabil: d.relatorio_contabil,
          fuso_horario: d.fuso_horario,
          resumo_por_email: d.resumo_por_email !== 'nao',
        }, e);
        atualizarEmpresa(atualizada);
        avisar('Dados da empresa salvos.');
        tela.ir('/conta', { substituir: true });
      },
    });
  } else {
    empresa = h('dl', { class: 'linha-dados' },
      h('dt', null, 'Nome'), h('dd', null, e.nome),
      e.nome_fantasia && [h('dt', null, 'Fantasia'), h('dd', null, e.nome_fantasia)],
      h('dt', null, 'Celular'), h('dd', null, formatarCelular(e.celular)),
      e.cidade && [h('dt', null, 'Cidade'), h('dd', null, [e.cidade, e.uf].filter(Boolean).join('/'))]);
  }

  const voce = h('div', { class: 'lista' },
    h('dl', { class: 'linha-dados' },
      h('dt', null, 'E-mail'), h('dd', null, sessao.usuario?.email ?? ''),
      h('dt', null, 'Papel'), h('dd', null, rotuloPapel(sessao.papel))),
    sessao.vinculos.length > 1 && h('a', { class: 'botao secundario cheio', href: '#/empresas' }, 'Trocar de empresa'),
    h('button', {
      class: 'botao perigo cheio',
      type: 'button',
      onClick: async () => {
        if (!(await confirmarSaida())) return;
        await sair();
        ir('/entrar', { substituir: true });
      },
    }, icone('sair'), 'Sair'));

  return casca({
    titulo: 'Conta',
    conteudo: [
      secao('Empresa', h('div', { class: 'lista' },
        dadosFixos,
        !contaGravavel() && faixa(ehTitular() ? 'Para voltar a gravar, veja a assinatura logo abaixo.' : 'Para voltar a gravar, o titular precisa regularizar a assinatura.', 'alerta'),
        ehTitular() && cartaoLink({
          href: '#/assinatura',
          simbolo: 'dinheiro',
          titulo: 'Assinatura',
          linhas: ['Assinar, ver e pagar as faturas, cancelar'],
        }),
        empresa)),
      secao('Usuários', h('div', { class: 'lista' },
        cartaoLink({
          href: '#/usuarios',
          simbolo: 'pessoas',
          titulo: 'Quem usa esta conta',
          linhas: [ehTitular() ? 'Convide o escritório e o contador' : 'Veja quem usa a conta ou saia dela'],
        }))),
      secao('Você', voce),
      secao('Seus dados', blocoDosDados()),
      h('p', { class: 'pequeno' }, `HSA Frota · versão ${__VERSAO__}`),
    ],
  });
}

// ---------------------------------------------------------------------
// Seus dados (LGPD): cópia de tudo, fotos por ano e pedido de exclusão (o
// titular); os textos dos Termos e da Política (todos).
// ---------------------------------------------------------------------
function blocoDosDados() {
  const e = sessao.empresa;
  const links = h('p', { class: 'pequeno links-legais' },
    h('a', { href: '#/termos' }, 'Termos de uso'), ' · ', h('a', { href: '#/privacidade' }, 'Política de privacidade'));
  if (!ehTitular()) {
    return h('div', { class: 'lista' },
      h('p', { class: 'pequeno' }, 'A cópia dos dados da empresa fica com o titular da conta. Para deixar de usar esta conta, vá em Usuários → "Sair desta empresa".'),
      links);
  }

  const comBotao = async (botao, acao) => {
    const antes = botao.textContent;
    botao.disabled = true;
    try {
      await acao((texto) => {
        botao.textContent = texto;
      });
    } catch (erro) {
      console.error(erro);
      avisar(mensagemDeErro(erro) || 'Não foi possível gerar o arquivo agora. Tente de novo.', 'erro');
    } finally {
      if (botao.isConnected) {
        botao.disabled = false;
        botao.textContent = antes;
      }
    }
  };

  const baixarCopia = (botao) => comBotao(botao, async (progresso) => {
    progresso('Gerando…');
    const { empresa, tabelas } = await dadosDaCopia();
    const arquivos = arquivosDaCopia({ empresa, tabelas, geradaEm: new Date().toLocaleString('pt-BR'), versao: __VERSAO__ });
    const blob = montarZip(arquivos, { nivel: 6 });
    const r = await entregarArquivo(blob, nomeDaCopia(empresa, hojeDaEmpresa()), 'Cópia dos dados do HSA Frota');
    if (r === 'baixado') avisar('Cópia dos dados baixada.');
  });

  const ano = campo({ rotulo: 'Ano das fotos', nome: 'ano', tipo: 'select', opcoes: [['', 'Carregando…']], desativado: true });
  const seletor = ano.querySelector('select');
  const botaoFotos = h('button', { class: 'botao cheio secundario', type: 'button', disabled: true }, 'Baixar as fotos dos comprovantes');
  let anexos = [];
  anexosDados.todos().then((lista) => {
    anexos = lista;
    const anos = anosDasFotos(lista);
    trocar(seletor, ...(anos.length ? anos.map((a) => h('option', { value: a }, a)) : [h('option', { value: '' }, 'Nenhuma foto ainda')]));
    seletor.disabled = anos.length === 0;
    botaoFotos.disabled = anos.length === 0;
  }).catch(() => {});

  botaoFotos.addEventListener('click', () => comBotao(botaoFotos, async (progresso) => {
    const fotos = fotosDoAno(anexos, seletor.value);
    const arquivos = [];
    const faltando = [];
    for (let i = 0; i < fotos.length; i++) {
      progresso(`Baixando as fotos (${i + 1} de ${fotos.length})…`);
      const blob = await anexosDados.baixar(fotos[i].anexo).catch(() => null);
      if (blob) arquivos.push({ nome: fotos[i].nome, dados: new Uint8Array(await blob.arrayBuffer()) });
      else faltando.push(fotos[i].nome);
    }
    if (faltando.length) {
      arquivos.push({
        nome: 'FALTANDO.txt',
        dados: new TextEncoder().encode(`Estas fotos não puderam ser baixadas (sem internet?). Baixe de novo com sinal:\r\n\r\n${faltando.join('\r\n')}\r\n`),
      });
    }
    const r = await entregarArquivo(montarZip(arquivos), nomeDaCopia(e, seletor.value).replace('-dados-', '-fotos-'), 'Fotos dos comprovantes');
    if (faltando.length) avisar(`${plural(faltando.length, 'foto ficou', 'fotos ficaram')} de fora (sem internet). A lista está no arquivo FALTANDO.txt.`, 'erro');
    else if (r === 'baixado') avisar('Fotos baixadas.');
  }));

  const assunto = `Pedido de exclusão dos dados - ${e.nome}`;
  const corpo = `Olá,\n\nPeço a exclusão dos dados da conta ${e.nome} (${e.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'} ${formatarDocumento(e.documento)}) no HSA Frota.\n\nE-mail de quem pede: ${sessao.usuario?.email ?? ''}\n`;

  return h('div', { class: 'lista' },
    h('p', { class: 'pequeno' }, 'Uma cópia de tudo o que a empresa lançou: uma planilha do Excel, com uma aba para cada assunto (caminhões, viagens, cargas, lançamentos, acertos…), e um arquivo para levar a outro sistema. Sai com o que está neste aparelho: se alguém lançou em outro aparelho, abra o app com internet antes.'),
    h('button', { class: 'botao cheio', type: 'button', onClick: (ev) => baixarCopia(ev.currentTarget) }, 'Baixar uma cópia dos dados'),
    h('p', { class: 'pequeno' }, 'As fotos dos comprovantes vão num arquivo .zip à parte, um ano por vez (as que não estão neste aparelho precisam de internet).'),
    ano,
    botaoFotos,
    h('p', { class: 'pequeno' },
      `Se a conta for cancelada, os dados ficam guardados por ${MESES_ATE_APAGAR} meses e depois são apagados. Para apagar antes, `,
      h('a', { href: `mailto:${RESPONSAVEL.email}?subject=${encodeURIComponent(assunto)}&body=${encodeURIComponent(corpo)}` }, 'peça a exclusão por e-mail'),
      '. Respondemos em até 15 dias.'),
    links);
}
